import {
  EditorText,
  EvergreenType,
  MediaTypePostEditorState,
  PostEditorLink,
  PostEditorState,
  PostGroupsType,
  RecurringType
} from '../../../models/post-editor';
import { makeFinalLink } from '../states/reducers/link.reducer';
import { deserializeText } from '../TextEditor/helpers/serializer';
import { PostLinkData } from '../../../hooks/usePostLinkSettings';
import { BaseLinkSettings } from '../TextEditor/helpers/onChange';
import {
  LinkDestination,
  LinkGoogleCallToAction,
  MediaProportions,
  MediaType,
  PostSource,
  PostTiming
} from '../../../models/graphql';
import { InitialPostEditorState } from '../states/initial';
import updateImmutable from 'immutability-helper';
import { MediaUploadStatus } from '../../../constants/media';
import { getUploadIdFromFileOrUrl } from '../../../helpers/media';
import { PostEditorErrorKeys } from '../../../models/validator-props';
import { validatorStateUpdater } from '../validators/helpers/validatorStateUpdater';
import { ErrorMessageSeverity } from '../../../constants/form';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { NewPostTimingType } from '../../../hooks/useOpenPostEditor';
import { WorkspaceSettingsForPostEditor } from '../../../hooks/usePostEditorNew';

const withLink = (
  url: string,
  linkSettings: PostLinkData,
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }
  const baseLinkSettings: BaseLinkSettings = {
    destination: LinkDestination.INITIAL,
    useBitly: !!linkSettings.defaultBitly,
    googleCallToAction: LinkGoogleCallToAction.NONE,
    utmTracking: linkSettings.utmTracking,
    addUtm: linkSettings.campaignTracking,
    removeUtm: linkSettings.shouldRemoveUtm,
    removePreview: false,
    linkMetadata: undefined
  };
  const link = {
    ...baseLinkSettings,
    initialUrl: url,
    url: makeFinalLink({ ...baseLinkSettings, initialUrl: url })
  };
  return updateImmutable(state, {
    postGroups: {
      [postGroupIndex]: {
        groupPostAttributes: {
          content: {
            link: {
              $set: link
            }
          }
        }
      }
    }
  });
};

const withExternalImages = (
  imageUrls: string[],
  uploadImmediately: boolean,
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  const mapExternalUrlToMedia = (url: string): MediaTypePostEditorState => ({
    __typename: 'Photo',
    id: getUploadIdFromFileOrUrl(url),
    type: MediaType.PHOTO,
    url,
    thumbnailUrl: url,
    proportions: MediaProportions.SQUARE,
    width: 0,
    height: 0,
    filesize: 0,
    loading: true,
    status: MediaUploadStatus.UPLOADING,
    gif: false,
    length: 0,
    transcoded: false,
    externalUpload: !uploadImmediately
  });

  return updateImmutable(state, {
    postGroups: {
      [postGroupIndex]: {
        groupPostAttributes: {
          content: {
            media: {
              $set: imageUrls.map(url => mapExternalUrlToMedia(url))
            }
          }
        }
      }
    }
  });
};

const withTexts = (
  texts: string[],
  link:
    | {
        url: string;
        linkSettings: PostLinkData;
      }
    | undefined = undefined,
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  let mappedLink: PostEditorLink | undefined = undefined;
  if (link) {
    const baseLinkSettings: BaseLinkSettings = {
      destination: LinkDestination.INITIAL,
      useBitly: !!link.linkSettings.defaultBitly,
      googleCallToAction: LinkGoogleCallToAction.NONE,
      utmTracking: link.linkSettings.utmTracking,
      addUtm: link.linkSettings.campaignTracking,
      removeUtm: link.linkSettings.shouldRemoveUtm,
      removePreview: false,
      linkMetadata: undefined
    };
    mappedLink = {
      ...baseLinkSettings,
      initialUrl: link.url,
      url: makeFinalLink({ ...baseLinkSettings, initialUrl: link.url })
    };
  }

  const editorTexts: EditorText[] = [];
  for (let i = 0; i < texts.length; i++) {
    const text = texts[i];
    // we must have 3rd party on
    editorTexts.push({ id: i + 1, text: deserializeText(text, [], mappedLink || null, true) });
  }

  return updateImmutable(state, {
    postGroups: {
      [postGroupIndex]: {
        groupPostAttributes: {
          content: {
            texts: {
              $set: editorTexts
            }
          }
        }
      }
    }
  });
};

const withSource = (source: PostSource, state: PostEditorState | undefined = undefined): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  return updateImmutable<PostEditorState>(state, {
    postGroups: {
      $apply: (currentGroups: PostGroupsType[]) =>
        currentGroups.map(curentGroup =>
          updateImmutable(curentGroup, {
            groupPostAttributes: {
              postSource: { $set: source }
            }
            // TODO once we finish the edit mode, we might need post for rescheduling in order to set the post source for
            // resheduled posts
            // posts: {
            //   $apply: (currentPosts: GroupPostAttributesType[]) =>
            //     currentPosts.map(post =>
            //       updateImmutable(post, {
            //         postSource: { $set: source }
            //       })
            //     )
            // }
          })
        )
    }
  });
};

const withInitialValidation = (
  severity: ErrorMessageSeverity,
  keys?: PostEditorErrorKeys[],
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  return validatorStateUpdater(state, postGroupIndex, severity, keys);
};

const withInitialProfiles = (
  profiles: SelectedSocialProfileValue[],
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  return updateImmutable(state, {
    postGroups: {
      [postGroupIndex]: {
        profiles: {
          $set: profiles
        }
      }
    }
  });
};

const withInitialCategory = (
  categoryId: string,
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  return updateImmutable(state, {
    postGroups: {
      [postGroupIndex]: {
        categoryId: {
          $set: categoryId
        }
      }
    }
  });
};

const withPostTiming = (
  workspaceSettings: WorkspaceSettingsForPostEditor,
  timing: NewPostTimingType,
  state: PostEditorState | undefined = undefined,
  postGroupIndex = 0
): PostEditorState => {
  const { evergreen, queuePosition, scheduledTimezone } = workspaceSettings;
  if (state === undefined) {
    state = InitialPostEditorState();
  }

  switch (timing.postTiming) {
    case PostTiming.QUEUE: {
      const { evergreen: evergreenFromPayload, expiryMaxRepeats, expiryDate } = timing;
      const expiryType: EvergreenType['expireType'] = [];
      if (typeof expiryMaxRepeats === 'number') {
        expiryType.push('count');
      }
      if (expiryDate) {
        expiryType.push('date');
      }
      return updateImmutable<PostEditorState>(state, {
        postGroups: {
          [postGroupIndex]: {
            groupPostAttributes: {
              scheduling: {
                postTiming: { $set: PostTiming.QUEUE },
                queuePosition: { $set: timing.queuePosition ? timing.queuePosition : queuePosition },
                evergreen: {
                  active: {
                    $set: evergreenFromPayload ? evergreenFromPayload : evergreen
                  },
                  expiryDate: {
                    $apply: (currentExpiry: EvergreenType['expiryDate']) => (expiryDate ? expiryDate : currentExpiry)
                  },
                  expiryMaxRepeats: {
                    $apply: (currentExpiry: EvergreenType['expiryMaxRepeats']) =>
                      expiryMaxRepeats ? expiryMaxRepeats : currentExpiry
                  },
                  expireType: {
                    $set: expiryType
                  }
                },
                recurring: {
                  active: { $set: false }
                },
                scheduledTimezone: { $set: scheduledTimezone }
              }
            }
          }
        }
      });
    }
    case PostTiming.RECURRING: {
      const { date, expiryDate, expiryMaxRepeats, recurringInterval, recurringValue } = timing;
      const expiryType: RecurringType['expireType'] = [];
      if (typeof expiryMaxRepeats === 'number') {
        expiryType.push('count');
      }
      if (expiryDate) {
        expiryType.push('date');
      }
      return updateImmutable(state, {
        postGroups: {
          [postGroupIndex]: {
            groupPostAttributes: {
              scheduling: {
                postTiming: { $set: PostTiming.RECURRING },
                evergreen: {
                  active: { $set: false }
                },
                recurring: {
                  active: { $set: true },
                  date: { $set: date },
                  expireType: { $set: expiryType },
                  expiryDate: {
                    $apply: (currentExpiry: EvergreenType['expiryDate']) => (expiryDate ? expiryDate : currentExpiry)
                  },
                  expiryMaxRepeats: {
                    $apply: (currentExpiry: EvergreenType['expiryMaxRepeats']) =>
                      expiryMaxRepeats ? expiryMaxRepeats : currentExpiry
                  }
                },
                recurringValue: {
                  $apply: currentValue => (recurringValue ? recurringValue : currentValue)
                },
                recurringType: {
                  $apply: currentValue => (recurringInterval ? recurringInterval : currentValue)
                },
                scheduledTimezone: { $set: scheduledTimezone }
              }
            }
          }
        }
      });
    }
    case PostTiming.SCHEDULE: {
      const { evergreen: evergreenFromPayload, expiryMaxRepeats, expiryDate } = timing;
      return updateImmutable(state, {
        postGroups: {
          [postGroupIndex]: {
            groupPostAttributes: {
              scheduling: {
                postTiming: { $set: PostTiming.SCHEDULE },
                evergreen: {
                  active: {
                    $set: evergreenFromPayload ? evergreenFromPayload : evergreen
                  },
                  expiryDate: {
                    $apply: (currentExpiry: EvergreenType['expiryDate']) => (expiryDate ? expiryDate : currentExpiry)
                  },
                  expiryMaxRepeats: {
                    $apply: (currentExpiry: EvergreenType['expiryMaxRepeats']) =>
                      expiryMaxRepeats ? expiryMaxRepeats : currentExpiry
                  }
                },
                scheduledLocalDateTimes: { $set: timing.date.map(d => ({ dateTime: d, isPast: false })) },
                scheduledTimezone: { $set: scheduledTimezone }
              }
            }
          }
        }
      });
    }
    case PostTiming.NOW: {
      const { evergreen: evergreenFromPayload, expiryMaxRepeats, expiryDate } = timing;
      return updateImmutable<PostEditorState>(state, {
        postGroups: {
          [postGroupIndex]: {
            groupPostAttributes: {
              scheduling: {
                postTiming: { $set: PostTiming.NOW },
                evergreen: {
                  active: {
                    $set: evergreenFromPayload ? evergreenFromPayload : evergreen
                  },
                  expiryDate: {
                    $apply: (currentExpiry: EvergreenType['expiryDate']) => (expiryDate ? expiryDate : currentExpiry)
                  },
                  expiryMaxRepeats: {
                    $apply: (currentExpiry: EvergreenType['expiryMaxRepeats']) =>
                      expiryMaxRepeats ? expiryMaxRepeats : currentExpiry
                  }
                },
                scheduledTimezone: { $set: scheduledTimezone }
              }
            }
          }
        }
      });
    }
  }
};

export const initialPostEditorStateBuilder = {
  withExternalImages,
  withInitialCategory,
  withInitialValidation,
  withInitialProfiles,
  withLink,
  withSource,
  withPostTiming,
  withTexts
};
